// components
import { Box, List, Tooltip, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

// hooks
import React from 'react';
import { useTranslation } from 'react-i18next';

// styles
import { StyledIconContainer } from '../FlexMatch.styles';
import {
  StyledCenteredTypography,
  StyledTooltipTitleBox,
  StyledValueAndIconButtonContainer,
  StyledVisibleIconButtonOnHover
} from './useGetColumns.styles';

// types
import type { GridColDef } from '@mui/x-data-grid-pro';
import type { CostCenters } from 'components/admin-portal/Tables/Employer/SurchargeRelations/types';
import type { FlexMatchTaskResponse } from 'RestAPIs/flex-match/types';

// utils
import { dateAndTimeFormatter, dateFormatter, timeFormatter } from 'utils/grid';
import { renderTooltipItem } from '../FlexMatch.utils';
import { getMatchStatusIcon, isOpenToAnAgency } from './useGetColumns.utils';

export const useGetColumns = ({
  costCenters,
  viewMode,
  onInfoClick
}: {
  costCenters: CostCenters[];
  viewMode: 'compact' | 'detailed';
  onInfoClick: (row: FlexMatchTaskResponse) => void; // NEW
}) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'flexMatch' });
  const { t: tCommon } = useTranslation('common');

  const getCostCenterName = (code: number) => {
    const costCenter = costCenters?.find((costCenter) => {
      return costCenter.brId === code;
    });
    return costCenter ? costCenter.name : '-';
  };

  const isCompactMode = viewMode === 'compact';

  const columns: GridColDef<FlexMatchTaskResponse>[] = [
    {
      headerName: tCommon('status'),
      field: 'flexPortalMatchFlags',
      type: 'string',
      width: 60,
      renderCell: ({ row }) => {
        const status = getMatchStatusIcon(row, t);
        if (!status) return '';

        const iconNode = (
          <StyledIconContainer color={status.color}>
            <Icon type={status.icon} />
          </StyledIconContainer>
        );

        return status.tooltip ? (
          <Tooltip title={status.tooltip}>{iconNode}</Tooltip>
        ) : (
          iconNode
        );
      }
    },
    {
      headerName: tCommon('costCenter'),
      field: 'costCenterName',
      type: 'string',
      ...(isCompactMode ? { flex: 1 } : { minWidth: 150, flex: 1 }),
      renderCell: ({ value, row }) => {
        if (!value) return '';

        return (
          <StyledValueAndIconButtonContainer>
            <Typography variant="body2" noWrap>
              {value}
            </Typography>
            <StyledVisibleIconButtonOnHover
              color="secondary"
              className="show-on-hover"
              size="small"
              data-testid="cost-center-info-button"
              onClick={(e) => {
                e.stopPropagation();
                onInfoClick(row);
              }}
            >
              <Icon type="circle-info" />
            </StyledVisibleIconButtonOnHover>
          </StyledValueAndIconButtonContainer>
        );
      }
    },
    {
      headerName: tCommon('date'),
      field: 'date',
      type: 'string',
      valueFormatter: dateFormatter,
      flex: 1
    },
    {
      headerName: tCommon('time'),
      field: 'dutyStartAndEndTime',
      type: 'string',
      flex: 1,
      valueFormatter: (params) => {
        return `${timeFormatter(params[0])} - ${timeFormatter(params[1])}`;
      },
      valueGetter: (_, row) => {
        const start = row.dutyStartTime;
        const end = row.dutyEndTime;
        return [start, end];
      }
    },
    {
      headerName: tCommon('dutyCode'),
      field: 'displayDutyCode',
      type: 'string',
      flex: 1,
      renderCell({ value, row }) {
        if (!value) return '';

        const periodsTime = row.periods
          ?.slice()
          .reverse()
          .map((period, index) => (
            <Box key={index}>
              {timeFormatter(period.startTime)} -{timeFormatter(period.endTime)}
            </Box>
          ));

        const qualifications = row.qualifications?.map((qual, index) => (
          <Box key={index}>{qual.name}</Box>
        ));

        const preferredEmployees = row.preferredEmployees?.map((emp, index) => (
          <Box key={index}>{emp.name}</Box>
        ));

        const notes = row.notes?.map((note, index) => (
          <Box key={index}>{note.content}</Box>
        ));

        const askedEmployees = row.askedEmployees?.map((emp, index) => (
          <Box key={index}>{emp.nameInformal}</Box>
        ));

        const agencyNotifications = row.agenciesNotifications?.map(
          (notification, index) => (
            <Box key={index}>
              {t('sentAt', {
                agencyName: notification.agencyName,
                sentTime: dateAndTimeFormatter(notification.sentTime)
              })}
            </Box>
          )
        );

        const actions = (actionType: 'SUBSCRIBE' | 'CONNECT') => {
          return row.employeeCostCenterActions
            ?.filter(({ action }) => action === actionType)
            .map((action, index) =>
              action.employeeCostCenterType === 'EXTERNAL' &&
              action.costCenterId ? (
                <Box key={index}>
                  {action.costCenterName ??
                    getCostCenterName(action.costCenterId)}
                </Box>
              ) : (
                <Box key={index}>
                  {t(`selfMatchActions.${action.employeeCostCenterType}`)}
                </Box>
              )
            );
        };

        const items: Array<[React.ReactNode, React.ReactNode]> = [
          [tCommon('period'), periodsTime],
          [tCommon('qualifications'), qualifications],
          [tCommon('preferredEmployees'), preferredEmployees],
          [tCommon('notes'), notes],
          [t('askedEmployees'), askedEmployees],
          [t('sentTo'), agencyNotifications],
          [t('openToConnectFor'), actions('CONNECT')],
          [t('openToApplyFor'), actions('SUBSCRIBE')]
        ];

        return (
          <Tooltip
            arrow
            slotProps={{ popper: { sx: { maxWidth: 440 } } }}
            title={
              <List dense>
                {items
                  .filter(([, content]) => React.Children.count(content) > 0)
                  .map(([label, content], idx, array) =>
                    renderTooltipItem(
                      label,
                      content,
                      idx,
                      idx === array.length - 1
                    )
                  )}
              </List>
            }
          >
            <Typography
              variant="body2"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {value}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      headerName: tCommon('function'),
      field: 'functionName',
      type: 'string'
    },
    {
      headerName: tCommon('employees'),
      field: 'preferredEmployees',
      type: 'string',
      width: 120,
      flex: 1,
      renderCell: (params) => {
        const employees: { name: string }[] = params.value || [];
        if (employees.length === 0) return '';
        const employeeNames = employees.map((emp) => emp.name).join(', ');

        if (employees.length > 1) {
          return (
            <Tooltip
              title={
                <StyledTooltipTitleBox>
                  {employees.map((emp, index) => (
                    <Box key={index}>{emp.name}</Box>
                  ))}
                </StyledTooltipTitleBox>
              }
              arrow
              slotProps={{
                popper: {
                  sx: {
                    maxWidth: 240
                  }
                }
              }}
            >
              <StyledCenteredTypography variant="body2">
                <StyledIconContainer noBackground color="secondary">
                  <Icon type="user-group" color="inherit" />
                </StyledIconContainer>
                ({employees.length})
              </StyledCenteredTypography>
            </Tooltip>
          );
        }

        return employeeNames;
      }
    },
    {
      headerName: t('columns.sendToFlex'),
      field: 'sentToFlexDate',
      type: 'string',
      width: 120,
      valueFormatter: dateAndTimeFormatter
    },
    {
      headerName: t('columns.employmentAgency'),
      field: 'agenciesNotifications',
      type: 'string',
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => {
        // Keyed on the match actions rather than on the mail records: the agency mail is sent after the
        // reply and nothing waits for it, so a request opened to an agency has no notification row yet.
        if (!isOpenToAnAgency(row)) return '';

        return (
          <StyledIconContainer noBackground color="secondary">
            <Icon type="briefcase" />
          </StyledIconContainer>
        );
      }
    },
    {
      headerName: t('columns.pool'),
      field: 'poolName',
      type: 'string'
    },
    {
      headerName: tCommon('reason'),
      field: 'reason',
      type: 'string'
    },
    {
      headerName: t('columns.nr'),
      field: 'requestId',
      type: 'string',
      width: 40
    }
  ];

  const compactHidden = new Set([
    'functionName',
    'sentToFlexDate',
    'agenciesNotifications',
    'poolName',
    'reason',
    'requestId'
  ]);

  const defaultColumnVisibilityModel: Record<string, boolean> = columns.reduce(
    (acc, col) => {
      const hideInCompact = compactHidden.has(col.field as string);
      acc[col.field as string] = viewMode === 'compact' ? !hideInCompact : true;
      return acc;
    },
    {} as Record<string, boolean>
  );

  return { columns, defaultColumnVisibilityModel };
};
